import { Box, Hidden, Typography, useTheme } from "@material-ui/core";
import { graphql } from "gatsby";
import get from "lodash/get";
import React, { useEffect, useState } from "react";
import HybridDropdown from "./HybridDropdown";
import {
  LargePriceTypography,
  MainTypography,
  PriceTypography
} from "./styles";
import { PriceComparisonFragment as Props } from "./__generated__/PriceComparisonFragment";

export default function PriceComparisonSection({ pricesJson }: Props) {
  const theme = useTheme();
  const prices = JSON.parse(pricesJson);

  const [quantity, setQuantity] = useState<string>("1");
  const [oilType, setOilType] = useState<string>("cured");

  const priceAirgraft = get(prices, ["airgraft", oilType, quantity], null);
  const price510Cart = get(prices, ["510_cart", oilType, quantity], null);
  const price510ClosedLoop = get(
    prices,
    ["closed_loop", oilType, quantity],
    null
  );
  const totalMonthlySavings =
    (price510Cart || price510ClosedLoop) - priceAirgraft;

  return (
    <Box display="flex" flexDirection="column" alignItems="stretch">
      {/* White questions box */}
      <Box
        padding={{ xs: 2, sm: 3 }}
        borderBottom={`1px solid ${theme.palette.grey[300]}`}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="grey.100"
        borderRadius="8px 8px 0 0"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="baseline"
          maxHeight={{ xs: "none", sm: "60px" }}
        >
          <MainTypography>I consume</MainTypography>
          <HybridDropdown
            options={[
              { value: "1", label: "1 gram/month" },
              { value: "2", label: "2 grams/month" },
              { value: "3", label: "3 grams/month" },
              { value: "4", label: "4 grams/month" },
              { value: "5", label: "5 grams/month" },
              { value: "6", label: "6 grams/month" },
              { value: "7", label: "7 grams/month" },
              { value: "8", label: "8 grams/month" },
              { value: "9", label: "9 grams/month" }
            ]}
            value={quantity}
            onChange={e => setQuantity(`${e.target.value}`)}
          />
        </Box>

        <Box
          display="flex"
          flexDirection="row"
          alignItems="baseline"
          maxHeight={{ xs: "none", sm: "60px" }}
        >
          <MainTypography>of</MainTypography>
          <HybridDropdown
            options={[
              { value: "cured", label: "Cured Resin" },
              { value: "live", label: "Live Resin" }
            ]}
            value={oilType}
            onChange={e => setOilType(`${e.target.value}`)}
          />
        </Box>

        <Box mt={3} textAlign="center">
          {oilType === "live" && (
            <Typography color="textSecondary">
              <span className="black">Papaya</span> by{" "}
              <span className="black">Friendly Farms</span> is used as the
              benchmark.
            </Typography>
          )}
          {oilType === "cured" && (
            <Typography color="textSecondary">
              <span className="black">Lemonade</span> by{" "}
              <span className="black">Friendly Farms</span> is used as the
              benchmark.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Grey prices box */}
      <Box
        bgcolor="grey.200"
        boxShadow="0px 5px 5px rgba(0, 0, 0, 0.1)"
        zIndex="100"
        paddingX={{ xs: 1, sm: 3 }}
        paddingY={{ xs: 2, sm: 3 }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {/* Airgraft price */}
        <Box textAlign="center" minWidth={{ xs: "90px", sm: "120px" }}>
          <Typography>Airgraft</Typography>
          <Box mt={0.5}>
            <PriceTypography>
              {priceAirgraft ? `$${priceAirgraft}` : "N/A"}
            </PriceTypography>
          </Box>
        </Box>

        {/* 510 cart price */}
        {price510Cart && (
          <Box
            textAlign="center"
            minWidth={{ xs: "90px", sm: "120px" }}
            marginX={{ xs: 2, sm: 8 }}
          >
            <Typography>510 Cart</Typography>
            <Box mt={0.5}>
              <PriceTypography style={{ color: theme.palette.grey[500] }}>
                {price510Cart ? `$${price510Cart}` : "N/A"}
              </PriceTypography>
            </Box>
          </Box>
        )}

        {/* Specialty pods price */}
        {price510ClosedLoop && (
          <Box textAlign="center" minWidth={{ xs: "90px", sm: "120px" }}>
            <Typography>Specialty Pods</Typography>
            <Box mt={0.5}>
              <PriceTypography style={{ color: theme.palette.grey[500] }}>
                {price510ClosedLoop ? `$${price510ClosedLoop}` : "N/A"}
              </PriceTypography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Green savings box */}
      <Box
        bgcolor="#0F9A2D"
        borderRadius="0 0 8px 8px"
        padding={{ xs: 2, sm: 3 }}
        minHeight={{ xs: 0, sm: "120px" }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Hidden xsDown implementation="css">
          <Typography variant="h5" className="white">
            Monthly savings
            <br />
            with Airgraft
          </Typography>
        </Hidden>
        <Hidden smUp implementation="css">
          <Typography variant="h6" className="white">
            Monthly saving
            <br />
            with Airgraft
          </Typography>
        </Hidden>

        <Box ml={4} display="flex" alignItems="center">
          <LargePriceTypography>
            {totalMonthlySavings && totalMonthlySavings > 0
              ? `$${totalMonthlySavings}+`
              : "N/A"}
          </LargePriceTypography>
        </Box>
      </Box>
    </Box>
  );
}

export const PriceComparisonFragment = graphql`
  fragment PriceComparisonFragment on DatoCmsCModulePriceComparison {
    pricesJson: prices
    container
    containerDisableGutters
    containerBackgroundColor
    innerContainer
    model {
      apiKey
    }
  }
`;
